import React from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { graphql, Link, StaticQuery } from 'gatsby';
import { Button, Typography } from 'amphitheatre';
import styled from 'styled-components';
import { StaticImage, IGatsbyImageData } from 'gatsby-plugin-image';

// @ts-ignore -- importing assets like this is supported, but TypeScript complains about missing module
import orbitVideo from 'src/assets/hero/saber_background_large.mp4';
import mdatLogo from 'src/images/mdat/mdat.svg';

import Layout from 'src/Layout';

import CallToAction from 'src/Components/CallToAction';
import Container from 'src/Components/ContentContainer';
import News from 'src/Components/News';
import SEO from 'src/Components/SEO';

import * as Base from 'src/page-styles/base.styled';
import * as Hero from 'src/page-styles/hero.styled';
import * as CustomButton from 'src/page-styles/button.styled';
import ProductCard from 'src/Components/ProductCard';

dayjs.extend(advancedFormat);

const { Header } = Typography;

interface Props {
  data: {
    allMarkdownRemark: {
      edges: Array<{
        node: {
          parent: {
            name: string;
            relativeDirectory: string;
          };
          frontmatter: {
            title: string;
            date: string;
            display_image: IGatsbyImageData;
          };
        };
      }>;
    };
    latest_events: {
      edges: Array<{
        node: {
          parent: {
            name: string;
            relativeDirectory: string;
          };
          frontmatter: {
            title: string;
            description: string;
            date: string;
          };
        };
      }>;
    };
  };
}

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media only screen and (min-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const Home: React.FunctionComponent<Props> = ({ data }: Props) => {
  const { allMarkdownRemark } = data;
  const { edges: prs } = allMarkdownRemark;

  return (
    <>
      <SEO
        title="Saber Astronautics | Cutting Edge Space Operations"
        description="Making space flight easy. Saber is a global provider of next generation space visualisation products and services for planning and operation missions."
      />
      <Layout>
        <Hero.Wrapper>
          <Hero.Cover>
            <Hero.Video
              className="hero-image"
              src={orbitVideo}
              autoPlay
              loop
              controls={false}
              playsInline
              muted
            />
          </Hero.Cover>
          <Hero.Text style={{ marginBottom: 420 }}>
            <Hero.MajorHeader style={{ letterSpacing: -3 }}>
              Making <Base.Yellow>Spaceflight</Base.Yellow> Easy
            </Hero.MajorHeader>
            <Hero.Header style={{ maxWidth: '1024px', margin: 'auto' }}>
              Now Flying <strong style={{ fontWeight: 900 }}>36 Tons</strong> On
              Orbit
            </Hero.Header>
            <Hero.Header style={{ maxWidth: '1024px', margin: 'auto' }}>
              Space Operations - Space Domain Awareness - Mission Software
            </Hero.Header>
            <Hero.Buttons>
              <Link to="/contact-us">
                <CustomButton.Yellow fill>Get in touch</CustomButton.Yellow>
              </Link>
              <Link to="#saber-products">
                <Button ghost fill>
                  Our products
                </Button>
              </Link>
            </Hero.Buttons>
          </Hero.Text>
        </Hero.Wrapper>
        <Container.Dark>
          <Base.Gap />
          <Base.ReverseGroup style={{ gap: 64, alignItems: 'center' }}>
            <Base.FlexItem>
              <Header level={1} style={{ fontWeight: 'bold' }}>
                Operations services to keep your mission flying high
              </Header>
              <Base.Paragraph>
                Saber provides follow-the-sun operations at its Responsive Space
                Operations Centers. We can provide night-shift services for your
                existing operations, or turn-key outsourced operations including
                game-changing software and ground station network.
              </Base.Paragraph>
              <Base.Group style={{ marginTop: 36, marginBottom: 32 }}>
                <Base.FlexItem>
                  <Header level={3}>Safety first</Header>
                  <Base.Paragraph>
                    Our technology provides real-time data and analytics for
                    analytics for mission success.
                  </Base.Paragraph>
                </Base.FlexItem>
                <Base.FlexItem>
                  <Header level={3}>Efficiency matters</Header>
                  <Base.Paragraph>
                    Streamline your operations with our intuitive space planning
                    tools.
                  </Base.Paragraph>
                </Base.FlexItem>
              </Base.Group>
              <Link to="/rsoc">
                <CustomButton.Yellow>Find out more</CustomButton.Yellow>
              </Link>
            </Base.FlexItem>
            <Base.FlexItem>
              <StaticImage
                className="rsoc-service-image"
                src="../images/rsoc.jpg"
                alt="The Responsive Space Operations Centre of Saber Astronautics"
              />
            </Base.FlexItem>
          </Base.ReverseGroup>
          <Base.Gap />
        </Container.Dark>
        <Container.Dark id="saber-products">
          <Header level={1} style={{ fontWeight: 'bold' }}>
            Cutting edge tools to revolutionize your mission
          </Header>
          <Base.Group style={{ alignItems: 'stretch', gap: 32 }}>
            <Base.FlexAlignedItem>
              <ProductCard
                name="Space Battle Management System"
                icon={
                  <StaticImage
                    className="space-cockpit-logo"
                    src="../images/space-cockpit/logo.png"
                    alt="Space Cockpit Logo"
                    height={100}
                  />
                }
                learnMoreHref="/sbms"
              >
                A modern space battle management platform that transforms
                complex mission data into actionable decision advantage.
                Combining advanced visualization, data fusion, automation, and
                AI-enabled decision support, SBMS helps operators understand the
                battlespace, coordinate missions, and respond rapidly to
                evolving threats. Operational today with more than 3,000 active
                users, SBMS gives teams the shared awareness they need to plan,
                coordinate, and act in complex space environments.
                <br />
                <br /> Designed for government and commercial space operations
                alike, SBMS is continuously evolving to meet tomorrow’s mission
                needs.
              </ProductCard>
            </Base.FlexAlignedItem>
            <Base.FlexAlignedItem>
              <ProductCard
                name={
                  <StaticImage
                    src="../images/logo_commander_centered.svg"
                    alt="Saber Commander"
                    height={48}
                  />
                }
                icon={
                  <StaticImage
                    className="pigi-logo"
                    src="../images/logo-yellow.png"
                    alt="Saber Commander Logo"
                    height={100}
                  />
                }
                learnMoreHref="/commander"
              >
                Commander is Saber’s mission control software for operating
                satellites and constellations with clarity, automation, and
                confidence. It transforms live spacecraft data into an intuitive
                visual operating environment, giving operators the context they
                need to understand spacecraft state, identify risks, and act
                quickly. With advanced automation features, mission planning,
                and real-time diagnostics, Commander helps reduce workload while
                supporting safer and more consistent mission execution.
                <br />
                <br /> From pre-flight testing through on-orbit service,
                Commander helps teams simplify complex missions and maintain
                safety of flight.
              </ProductCard>
            </Base.FlexAlignedItem>
            <Base.FlexAlignedItem>
              <ProductCard
                name="Responsive Space Operations Center"
                icon={
                  <StaticImage
                    className="space-cockpit-logo"
                    src="../images/saber-rsoc-patch.png"
                    alt="Space Cockpit Logo"
                    height={100}
                  />
                }
                learnMoreHref="/rsoc"
              >
                Ensure your safety of flight with Saber Astronautics’ mission
                operations and integrated space traffic management services.
                From the United States and Australia, Saber’s Responsive Space
                Operations Centers deliver continuous support for spacecraft
                operations in an increasingly complex orbital environment. Our
                operators combine mission execution, ground segment
                coordination, and space traffic awareness to help customers
                protect their spacecraft and mission objectives.
                <br />
                <br />
                With 33 tons of satellites currently under operation, Saber
                brings proven operational depth to missions that need
                experienced hands on console.
              </ProductCard>
            </Base.FlexAlignedItem>
          </Base.Group>
          <Base.Gap />
        </Container.Dark>
        <Container.Dark>
          <Header level={1} style={{ fontWeight: 'bold' }}>
            See what we&apos;ve been up to
          </Header>
          <Base.Paragraph style={{ marginBottom: 36 }}>
            Stay in the loop with our latest news! Catch up on Saber highlights
            and get a sneak peek into how we're pushing the boundaries in the
            space industry globally. Follow our journey and get in touch to be a
            part of it!
          </Base.Paragraph>
          <ContentWrapper>
            {prs.map((pr, index) => {
              const { title, date, display_image } = pr.node.frontmatter;
              const { name, relativeDirectory } = pr.node.parent;

              return (
                <News
                  key={`${title}-${index}`}
                  href={`/${relativeDirectory}/${name}/`}
                  title={title}
                  date={date}
                  displayImage={display_image}
                />
              );
            })}
          </ContentWrapper>
        </Container.Dark>
        <CallToAction
          header="LET'S BEGIN"
          linkText="Get in contact today"
          to="/contact-us/"
        />
      </Layout>
    </>
  );
};

export default (): JSX.Element => (
  <StaticQuery
    query={graphql`
      query IndexQuery {
        allMarkdownRemark(
          limit: 3
          sort: { fields: frontmatter___date, order: DESC }
          filter: { frontmatter: { type: { eq: "pr" } } }
        ) {
          edges {
            node {
              parent {
                ... on File {
                  name
                  relativeDirectory
                }
              }
              frontmatter {
                title
                date
                display_image {
                  childImageSharp {
                    gatsbyImageData(
                      layout: FULL_WIDTH
                      placeholder: BLURRED
                      formats: [AUTO, WEBP, AVIF]
                    )
                  }
                }
              }
            }
          }
        }

        latest_events: allMarkdownRemark(
          limit: 2
          sort: { fields: frontmatter___date, order: ASC }
          filter: {
            frontmatter: { type: { eq: "events" } }
            isFuture: { eq: true }
          }
        ) {
          edges {
            node {
              frontmatter {
                title
                description
                date
              }
            }
          }
        }
      }
    `}
    render={(data) => <Home data={data} />}
  />
);
